// Import dependencies
import {  html, render } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { getTokenParts } from '@automator/shared/utils/get-token-parts';
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import definitions
import { getUniversalToken } from '@automator/builder/definitions/items/token';

import Tokens from '../';

/**
 * Creates an Universal Token
 */
class UniversalToken {
	/**
	 * The token prefix. This is what we're going to use to identify the token type.
	 * 
	 * @type {String}
	 */
	_prefix = 'UT';
		
	/**
	 * Constructs an instance of UniversalToken with a given token ID.
	 * 
	 * @param {String} tokenID The full token ID string following the format "TYPE:INTEGRATIONCODE:BASE:..."
	 */
	constructor( tokenID, options = {} ) {
		this.id = tokenID;
		this.options = options;
	}

	/**
	 * Callback invoked before adding the token to the field
	 *
	 * @param {Function} callback Callback invoked after validating fields
	 * @return {undefined}
	 */
	beforeAdded( callback, targetField ) {
		// Show the options
		this.renderOptions( response => {
			callback( { ...this.directoryData, ...{
				id: response.updatedID
			} } );
		} );
	}

	/**
	 * Renders the dialog for editing the token
	 * This method creates a new node in the DOM to render the dialog component.
	 * 
	 * @param {Function} callback - A callback function to be executed when the dialog is closed.
	 * @returns {undefined}
	 */
	renderOptions( callback ) {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uap-builder-item-token-edit-dialog
					token-code=${ this.tokenCode }
					integration-code=${ this.integrationCode }
					item-id=${ ifDefined( this.options.itemID ) }
					.fields=${ this.fieldsFromID }

					@uap-builder-item-token-saved-successfully=${ event => {
						// Invoke the callback
						callback( {
							updatedID: this.createUpdatedID( 
								event.detail.unsavedFieldValues 
							)
						} );
					} }
				></uap-builder-item-token-edit-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * Creates an updated token ID using the directoryData idTemplate and provided updatedFields.
	 * 
	 * This method constructs a new token ID by inserting updated field values into the idTemplate structure.
	 * Each part identified by the idTemplate is replaced by its corresponding updated value from updatedFields, if available; otherwise, the original value is retained.
	 * 
	 * @param {Object} updatedFields - An object containing the updated field values, where each value is stored under a key with the same name as the field.
	 * @returns {String} The updated token ID string, formatted according to the idTemplate.
	 */
	createUpdatedID( updatedFields ) {
		// Generate the base parts of the token ID
		const baseParts = [ this._prefix, this.integrationCode, this.tokenCode ];

		// Generate the dynamic part of the token ID based on updated fields
		const dynamicParts = this.directoryData.idTemplate.split( ':' ).map( part => {
			// Use the updated value if available; otherwise, fall back to the original value.
			let partValue = updatedFields.hasOwnProperty( part ) 
				? updatedFields[ part ].value 
				: this.fieldsFromID[ part ]?.value || '';

			// Use the updated value if available; otherwise, fall back to the original value.
			return partValue;
		});

		// Combine base parts with updated dynamic parts to form the complete updated token ID
		const updatedID = [ ...baseParts, ...dynamicParts ].join( ':' );

		return updatedID;
	}

	/**
	 * Generates a dynamic token name by replacing placeholders in the name template with values derived from the token ID.
	 * 
	 * @returns {String} The generated name with placeholders filled in.
	 */
	get dynamicTokenName() {
		// Use directoryData to check if a name template is provided
		if ( ! this.directoryData.nameTemplate ) {
			return this.directoryData.name; // Use the default name if no template is provided
		}

		const fieldsFromID = this.fieldsFromID;

		// Replace placeholders in the template with corresponding values from fieldsFromID
		return this.directoryData.nameTemplate.replace( /\{\{(\w+)\}\}/g, ( match, key ) => {
			// Use the field value if available; otherwise, fall back to the original placeholder
			let fieldValue = isDefined( fieldsFromID[ key ] ) 
				? fieldsFromID[ key ].value 
				: match;

			// Render tokens
			fieldValue = new Tokens().renderTextWithTokens( fieldValue, { returnString: true } );

			return fieldValue;
		});
	}

	/**
	 * Extracts the integration code from a token ID.
	 * 
	 * The token ID is expected to follow a specific format where the integration code is the second part of a colon-separated string. This function parses the token ID and returns the integration code.
	 * 
	 * @returns {String} The extracted integration code.
	 */
	get integrationCode() {
		// Get the token parts
		const parts = getTokenParts( this.id );
		// Assuming the integration code is always the second part of the token ID
		const integrationCode = parts[1];

		return integrationCode;
	}

	/**
	 * Extracts the token code from a token ID.
	 * 
	 * The token ID is expected to follow a specific format where the token base is the third part of a colon-separated string. This function parses the token ID and returns the token base.
	 * 
	 * @returns {String} The extracted token base.
	 */
	get tokenCode() {
		// Get the token parts
		const parts = getTokenParts( this.id );

		// Assuming the token base is always the third part of the token ID
		const tokenCode = parts[2];

		return tokenCode;
	}

	/**
	 * Returns the directory data of this UniversalToken
	 * 
	 * @return {Object} The directory data
	 */
	get directoryData() {
		return getUniversalToken(
			this.integrationCode, 
			this.tokenCode 
		);
	}

	/**
	 * Parses the token ID to extract fields and their values, using the idTemplate to determine the order.
	 * 
	 * This method ensures that fields are correctly mapped according to the order specified in the idTemplate, rather than assuming a fixed order. This approach is necessary because the field order in the token ID may not always match the order of fields in the directoryData object.
	 * 
	 * @returns {Object} An object with field values and special properties for integration code and token base.
	 */
	get fieldsFromID() {
		// If the idTemplate is not defined, we can assume that the token ID is the same as the token base
		if ( ! this.directoryData.idTemplate ) {
			return {};
		}

		// Split the token
		const parts = getTokenParts( this.id );

		// Assume the first three parts are "TYPE", "INTEGRATIONCODE", and "BASE", and the rest are dynamic fields
		const dynamicFields = parts.slice(3);

		const result = {};

		// Process the dynamic fields based on the idTemplate
		const templateParts = this.directoryData.idTemplate.split( ':' );
	
		templateParts.forEach( ( templatePart, index ) => {
			// Extract the field key from the template and assign the corresponding value from the dynamic fields
			const fieldKey = templatePart;
			// Ensure we don't exceed the bounds of the dynamicFields array
			if ( index < dynamicFields.length ) {
				result[ fieldKey ] = {
					value: dynamicFields[ index ],
				};
			}
		} );

		return result;
	}

	/**
	 * Checks if the tokenID is a UniversalToken
	 *
	 * @param {String} tokenID The token ID
	 * @return {Boolean} True if the tokenID is a UniversalToken
	 */
	is( tokenID ) {
		return tokenID.startsWith( `${ this._prefix }:${ this.integrationCode }:${ this.tokenCode }` );
	}

	/**
	 * Returns a variation of this token given a token ID with custom data
	 * We use this method to create a new instance of the UniversalToken class with custom data.
	 *
	 * @param {String} tokenID The token ID
	 * @return {PostMetaToken} The Post Meta token, with custom data
	 */
	getVariation( tokenID ) {
		// Create a new instance of this class
		return new UniversalToken( tokenID );
	}

	/**
	 * Returns the name of the token
	 * 
	 * @return {HTMLString} The name of the token
	 */
	get name() {
		// If it has field values, return the dynamic token name
		return ! isEmpty( this.fieldsFromID ) ? this.dynamicTokenName : this.directoryData.name;
	}

	/**
	 * Returns whether the token requires user data
	 * 
	 * @return {Boolean} True if the token requires user data
	 */
	get requiresUser() {
		return this.directoryData.requiresUser;
	}
}

export default UniversalToken;